import {createContext, ReactNode, useContext, useState} from 'react'

interface SessionContextType {
    addToSession: (key: string, value: string) => void;
    addJsonToSession: (key: string, value: object) => void;
    getFromSession: (key: string) => string | null;
    getJsonFromSession: (key: string) => object | undefined;
    removeFromSession: (key: string) => void;
    sessionState: Record<string, string>;
}

const SessionContext = createContext<SessionContextType>({} as SessionContextType)
const useSessionContext = () => useContext(SessionContext)

interface Props {
    children: ReactNode
}

const SessionProvider = (props: Props) => {
    const [sessionState, setSessionState] = useState<Record<string, string>>(() => {
        const initialState: Record<string, string> = {};
        if (typeof window !== 'undefined') {
            for (let i = 0; i < window.sessionStorage.length; i++) {
                const key = window.sessionStorage.key(i);
                if (key) {
                    initialState[key] = window.sessionStorage.getItem(key) || '';
                }
            }
        }
        return initialState;
    });

    const addToSession = (key: string, value: string) => {
        if (key && typeof window !== "undefined") {
            window.sessionStorage.setItem(key, value);
            const updatedSessionState: Record<string, string> = {};
            for (let i = 0; i < window.sessionStorage.length; i++) {
                const storageKey = window.sessionStorage.key(i);
                if (storageKey) {
                    updatedSessionState[storageKey] = window.sessionStorage.getItem(storageKey) || '';
                }
            }
            setSessionState(updatedSessionState);
        }
    }

    const addJsonToSession = (key: string, value: object) => {
        if (value) {
            const json = JSON.stringify(value);
            addToSession(key, json);
        }
    }

    const getFromSession = (key: string) => {
        if (key && typeof window !== "undefined") {
            return window.sessionStorage.getItem(key);
        }
        return "";
    };

    const getJsonFromSession = (key: string) => {
        const str = getFromSession(key)
        if (str) {
            return JSON.parse(str);
        }
        return undefined;
    };

    const removeFromSession = (key: string) => {
        if (key && typeof window !== "undefined") {
            window.sessionStorage.removeItem(key);
            const updatedSessionState: Record<string, string> = {};
            for (let i = 0; i < window.sessionStorage.length; i++) {
                const storageKey = window.sessionStorage.key(i);
                if (storageKey) {
                    updatedSessionState[storageKey] = window.sessionStorage.getItem(storageKey) || '';
                }
            }
            setSessionState(updatedSessionState);
        }
    }
    return (
        <SessionContext.Provider
            value={{addToSession, addJsonToSession, getFromSession, getJsonFromSession, removeFromSession, sessionState}}>
            {props.children}
        </SessionContext.Provider>
    )
}


export {
    useSessionContext,
    SessionProvider
}